/**
 * Notices — committee announcements in, member suggestions out.
 *
 * Opening this tab marks the notices read, which is what clears the bell.
 */

import { useEffect, useState } from 'react';
import { timeAgo } from '../../data';
import { useClubData } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { Button, Empty, Eyebrow, INPUT_CLS, ViewTitle } from '../../components/ui';

/* Message card: white, left rule coloured by kind. */
const MSG_CARD = 'border border-line border-l-[3px] bg-white px-4 py-3.5';
// kind: 'notice' | 'suggestion' | 'reply'
const MSG_RULE = {
  notice: 'border-l-gold-500',
  suggestion: 'border-l-line-strong',
  reply: 'border-l-sea-600',
};
const MSG_TAG = {
  notice: 'bg-gold-50 text-gold-800',
  suggestion: 'bg-line-chip text-copy-muted',
  reply: 'bg-sea-50 text-sea-600',
};
const MSG_HEAD = 'flex flex-wrap items-baseline justify-between gap-2.5';
const MSG_FROM = 'font-market text-[15px] font-bold tracking-[-0.015em]';
const MSG_WHEN = 'font-market text-[9.5px] text-copy-label';
const MSG_BODY = 'mt-2 text-[13.5px] leading-relaxed text-copy-muted';

export function NoticesPage() {
  const { db, meId, sendSuggestion, markNoticesRead } = useClubData();
  const { toast } = useUI();
  const [draft, setDraft] = useState('');

  // Reading the tab is what marks them read.
  useEffect(() => {
    void markNoticesRead();
    // Once per visit. Re-running on every message would fight the badge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const notices = db.messages
    .filter((m) => m.to === 'all' || m.to === meId)
    .sort((a, b) => b.ts - a.ts);

  const mine = db.messages.filter((m) => m.from === meId).sort((a, b) => b.ts - a.ts);

  async function send() {
    if (!draft.trim()) {
      toast('Write your suggestion first');
      return;
    }
    await sendSuggestion(draft.trim());
    setDraft('');
    toast('Sent to the committee');
  }

  return (
    <>
      <ViewTitle
        title="Notices & suggestions"
        sub="Announcements from the committee — and your ideas back to them"
      />

      <Eyebrow>From the committee</Eyebrow>
      {notices.length ? (
        notices.map((m) => (
          <div key={m.id} className={`mb-2.5 ${MSG_CARD} ${MSG_RULE[m.kind] ?? MSG_RULE.notice}`}>
            <div className={MSG_HEAD}>
              <div className={MSG_FROM}>{m.from === 'admin' ? 'Committee' : 'Club'}</div>
              <div>
                <span
                  className={`rounded-full px-1.5 py-0.5 font-market text-[8.5px] uppercase tracking-[0.1em] ${MSG_TAG[m.kind] ?? MSG_TAG.notice}`}
                >
                  Notice
                </span>{' '}
                <span className={MSG_WHEN}>{timeAgo(m.ts)}</span>
              </div>
            </div>
            <div className={MSG_BODY}>{m.text}</div>
          </div>
        ))
      ) : (
        <Empty>No notices yet.</Empty>
      )}

      <Eyebrow>Send a suggestion</Eyebrow>
      <div className={`${MSG_CARD} ${MSG_RULE.suggestion}`}>
        <div className="mb-2.5 text-[13.5px] leading-relaxed text-copy-muted">
          Something that would make the club better? Route ideas, timings, events — the committee
          reads every one.
        </div>
        <textarea
          className={`${INPUT_CLS} min-h-[90px] resize-y leading-normal`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. Can we add a water point at the 5 km mark?"
        />
        <div className="mt-3 flex gap-2">
          <Button tone="p" onClick={send}>
            Send to committee
          </Button>
        </div>
      </div>

      {mine.length > 0 && (
        <>
          <Eyebrow>Your past suggestions</Eyebrow>
          {mine.map((m) => (
            <div key={m.id} className={`mb-2.5 ${MSG_CARD} ${MSG_RULE.suggestion}`}>
              <div className={MSG_HEAD}>
                <div className={MSG_FROM}>You</div>
                <span className={MSG_WHEN}>{timeAgo(m.ts)}</span>
              </div>
              <div className={MSG_BODY}>{m.text}</div>

              {(m.replies ?? []).map((r, i) => (
                <div key={i} className={`mt-3 ${MSG_CARD} ${MSG_RULE.reply}`}>
                  <div className={MSG_HEAD}>
                    <div className={MSG_FROM}>Committee replied</div>
                    <span className={MSG_WHEN}>{timeAgo(r.ts)}</span>
                  </div>
                  <div className={MSG_BODY}>{r.text}</div>
                </div>
              ))}

              {!(m.replies ?? []).length && (
                <div className={`mt-2 ${MSG_WHEN}`}>Awaiting a reply</div>
              )}
            </div>
          ))}
        </>
      )}
    </>
  );
}
